import { useState } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, X, ShoppingBag, Instagram } from "lucide-react";
import { BRAND, LOGOS } from "@/brand";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { label: "SHOP", href: "#shop" },
  { label: "VAULTS", href: "#vaults" },
  { label: "MANIFESTO", href: "#manifesto" },
  { label: "LOOKBOOK", href: "#lookbook" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { count, setIsOpen } = useCart();
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  const go = (href) => {
    setMenuOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        data-testid="navbar-header-main"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 2.6, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? "bg-[#0A0A0B]/85 backdrop-blur-xl border-b border-zinc-800/60" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-8 py-4">
          <button
            data-testid="header-brand-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-3 text-left"
          >
            <img
              src={LOGOS.wordmark}
              alt={BRAND.name}
              className="logo-blend hidden md:block h-12 w-auto"
            />
            <img
              src={LOGOS.monogram}
              alt={BRAND.short}
              className="logo-blend md:hidden h-9 w-auto"
            />
          </button>

          <nav className="hidden md:flex items-center gap-8" data-testid="header-nav-links">
            {LINKS.map((l) => (
              <button
                key={l.label}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => go(l.href)}
                className="text-[11px] tracking-[0.3em] text-zinc-400 hover:text-zinc-100 transition-colors duration-300 uppercase"
              >
                {l.label}
              </button>
            ))}
            <a
              data-testid="nav-link-instagram"
              href={BRAND.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-500 hover:text-zinc-100 transition-colors duration-300"
            >
              <Instagram className="h-4 w-4" />
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="cart-drawer-toggle"
              onClick={() => setIsOpen(true)}
              className="relative flex items-center gap-2 border border-zinc-700/70 px-4 py-2 text-[11px] tracking-[0.25em] text-zinc-200 uppercase hover:border-zinc-400 hover:bg-zinc-100 hover:text-zinc-950 transition-colors duration-300"
            >
              <ShoppingBag className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Cart</span>
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center bg-red-900 text-[10px] font-bold text-zinc-100">
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden text-zinc-300 p-1"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu-drawer"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-x-0 top-[64px] z-40 border-b border-zinc-800 bg-[#0A0A0B]/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.label}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="text-left font-display text-2xl tracking-[0.15em] text-zinc-200 uppercase"
                >
                  {l.label}
                </button>
              ))}
              <a
                data-testid="mobile-nav-link-instagram"
                href={BRAND.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] tracking-[0.3em] text-zinc-500 uppercase"
              >
                {BRAND.instagramHandle}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
